import React, { useEffect, useMemo, useState } from "react";

import { Link, useSearchParams } from "react-router-dom";

import axios from "../../api/axiosinstance";

import Pagination from "../../components/partials/Pagination";

import RowsPerPage from "../../components/partials/RowsPerPage";

import Auth from "../../utils/AuthUtils";

function List() {
  const [projects, setProjects] = useState([]);

  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({});

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );

  /* =========================================
     CURRENT USER ROLE
  ========================================= */

  const role = Auth.getUserDetails()?.role?.toLowerCase()?.trim();

  /* =========================================
     QUERY
  ========================================= */

  const query = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,

      limit: Number(searchParams.get("limit")) || 10,

      search: searchParams.get("search") || "",

      projectStatus: searchParams.get("projectStatus") || "",

      priority: searchParams.get("priority") || "",
    }),

    [searchParams],
  );

  /* =========================================
     UPDATE FILTER
  ========================================= */

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    if (key !== "page") {
      params.set("page", "1");
    }

    setSearchParams(params);
  };

  /* =========================================
     FETCH PROJECTS
  ========================================= */

  const fetchProjects = async () => {
    try {
      setLoading(true);

      const res = await axios.get("/projects/list", {
        params: query,
      });

      console.log("PROJECT LIST RESPONSE:", res);

      if (res?.code === 200) {
        const projectData = res?.data?.projects || [];

        setProjects(Array.isArray(projectData) ? projectData : []);

        setPagination(res?.data?.pagination || {});
      } else {
        setProjects([]);
        setPagination({});
      }
    } catch (error) {
      console.error("PROJECT LIST ERROR:", error);

      setProjects([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     SEARCH DEBOUNCE
  ========================================= */

  useEffect(() => {
    const timer = setTimeout(() => {
      updateFilter("search", searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* =========================================
     FETCH ON FILTER CHANGE
  ========================================= */

  useEffect(() => {
    fetchProjects();
  }, [searchParams]);

  /* =========================================
     GET PROJECT VIEW URL
  ========================================= */

  const getProjectUrl = (projectId) => {
    if (role === "developer") {
      return `/developer-project/${projectId}`;
    }

    if (role === "tester" || role?.includes("tester")) {
      return `/testing-project/${projectId}`;
    }

    return `/project/${projectId}`;
  };

  return (
    <div className="content">
      {/* =====================================
          HEADER
      ====================================== */}

      {/* <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">🚀 Projects</h3>

          <p className="text-muted mb-0">All assigned projects</p>
        </div>

        <span className="badge bg-primary fs-12 px-3 py-2">
          {pagination?.total || 0} Projects
        </span>
      </div> */}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">🚀 Projects</h3>

          <p className="text-muted mb-0">All assigned projects</p>
        </div>

        <div className="d-flex align-items-center gap-2">
          {role === "developer" && (
            <Link to="/create-project" className="btn btn-primary">
              <i className="ti ti-plus me-1"></i>
              New Project
            </Link>
          )}

          <span className="badge bg-primary fs-12 px-3 py-2">
            {pagination?.total || 0} Projects
          </span>
        </div>
      </div>

      {/* =====================================
          FILTERS
      ====================================== */}

      <div className="row mb-3">
        {/* SEARCH */}

        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* STATUS */}

        <div className="col-md-3">
          <select
            className="form-select"
            value={searchParams.get("projectStatus") || ""}
            onChange={(e) => updateFilter("projectStatus", e.target.value)}
          >
            <option value="">All Status</option>

            <option value="pending">Pending</option>

            <option value="in_progress">In Progress</option>

            <option value="testing">Testing</option>

            <option value="completed">Completed</option>
          </select>
        </div>

        {/* PRIORITY */}

        <div className="col-md-3">
          <select
            className="form-select"
            value={searchParams.get("priority") || ""}
            onChange={(e) => updateFilter("priority", e.target.value)}
          >
            <option value="">All Priority</option>

            <option value="low">Low</option>

            <option value="medium">Medium</option>

            <option value="high">High</option>
          </select>
        </div>
      </div>

      {/* =====================================
          TABLE
      ====================================== */}

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">Loading Projects...</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5 text-muted">No Projects Found</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>

                    <th>Project</th>

                    <th>Client</th>

                    <th>Developer</th>

                    <th>Tester</th>

                    <th>Priority</th>

                    <th>Status</th>

                    <th>Deadline</th>

                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {projects.map((item, index) => (
                    <tr key={item._id}>
                      <td>
                        {index +
                          1 +
                          ((pagination?.page || 1) - 1) *
                            (pagination?.limit || 10)}
                      </td>

                      <td>
                        <strong>{item?.projectName || "-"}</strong>
                      </td>

                      <td>{item?.leadId?.name || "-"}</td>

                      <td>{item?.assignedDeveloper?.name || "-"}</td>

                      <td>{item?.assignedTester?.name || "-"}</td>

                      <td>
                        <span
                          className={`badge ${
                            item.priority === "high"
                              ? "bg-danger"
                              : item.priority === "medium"
                                ? "bg-warning text-dark"
                                : "bg-success"
                          }`}
                        >
                          {item.priority}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`badge ${
                            item.projectStatus === "completed"
                              ? "bg-success"
                              : item.projectStatus === "testing"
                                ? "bg-info"
                                : item.projectStatus === "in_progress"
                                  ? "bg-primary"
                                  : "bg-warning text-dark"
                          }`}
                        >
                          {item.projectStatus}
                        </span>
                      </td>

                      <td>
                        {item.deadline
                          ? new Date(item.deadline).toLocaleDateString()
                          : "-"}
                      </td>

                      <td>
                        <Link
                          to={
                            role === "developer"
                              ? `/developer-project/${item._id}`
                              : role === "tester"
                                ? `/testing-project/${item._id}`
                                : `/project/${item._id}`
                          }
                          className="btn btn-sm btn-primary"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* PAGINATION */}

              <div className="row mt-3">
                <RowsPerPage
                  value={searchParams.get("limit") || "10"}
                  onChange={(val) => updateFilter("limit", val)}
                />

                <Pagination
                  pagination={pagination}
                  updateFilter={updateFilter}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default List;
